import dataHubManager, { DataHubOperationType, DataHubStorageOperations } from '../../managers/data/DataHubManager'
import dataStorageManager, { BucketDefaultIDs } from '../../managers/data/DataStorageManager'
import { IArmador, OperatedArmador } from '../api-services/ArmadoresServices'
import { IChange, RequestedChange } from '../api-services/ChangesServices'
import { IEmbarcacion, OperatedEmbarcacion } from '../api-services/EmbarcacionesServices'
import { IRequest, RequestObjectType, RequestStatus } from '../api-services/RequestsServices'
import { ITripulante, OperatedTripulante } from '../api-services/TripulantesServices'
import { IUser } from '../api-services/UsersServices'
import { IViaje, OperatedViaje, requestedTripulante } from '../api-services/ViajesServices'
import { rutify } from '../apiUtils'
import { changesHubName } from './ChangesHub'
import dataHubServices, { defaultRefreshTime, StorageService } from './DataHubServices'

export enum ViajesHubPipelines {
  VIAJES_WITH_CORRECTED_DATES = 'viajes_with_corrected_dates', // Pipeline para mostrar los viajes con sus fechas corregidas en base a las marcas de inicio, fin y salida
  VIAJES_WITH_REQUESTS = 'viajes_with_requests', // Pipeline para mostrar los viajes con su solicitud más reciente (si existe)}
  VIAJES_WITH_POSICIONAMIENTOS = 'viajes_with_posicionamientos', // Pipeline para mostrar los viajes con su posicionamientos relacionados
  VIAJES_WITH_ASOCIATIONS = 'viajes_with_asociations', // Pipeline para mostrar los viajes con sus asociaciones relacionadas
  VIAJES_WITH_EMBARCACION = 'viajes_with_embarcacion', // Pipeline para mostrar los viajes con su embarcacion relacionada
  VIAJES_WITH_PATRON = 'viajes_with_patron', // Pipeline para mostrar los viajes con su patron relacionado
  VIAJES_WITH_ARMADOR = 'viajes_with_armador', // Pipeline para mostrar los viajes con su armador relacionado
  VIAJES_WITH_TRIPULANTES = 'viajes_with_tripulantes', // Pipeline para mostrar los viajes con sus tripulantes relacionados
  VIAJES_WITH_PERMISO_MUELLE_WESTE = 'viajes_with_permiso_muelle_weste', // Pipeline para mostrar los viajes con su permiso de muelle oeste relacionado
  VIAJES_WITH_COBRO = 'viajes_with_cobro', // Pipeline para mostrar los viajes con su cobro relacionado
  VIAJES_WITH_COBRO_REQUEST = 'viajes_with_cobro_request', // Pipeline para mostrar los viajes con su solicitud de cobro relacionada
  VIAJES_FULL = 'viajes_full', // Pipeline completo para mostrar toda la información relacionada a un viaje (solicitud, posicionamientos, asociación, embarcacion, patron, armador, tripulantes, permiso de muelle oeste, cobros, etc)
}

export const viajesHubName = 'viajesHub'

export class ViajesHub implements StorageService {
  bucketData: any[] = []

  storageOperations: DataHubStorageOperations<IEmbarcacion> = {
    getData: () => [],
    markDirty: () => {},
    markStaled: () => {},
    forceRefresh: async () => {},
  }
  pipelinesOperations: Map<ViajesHubPipelines, DataHubStorageOperations<any>> = new Map()

  constructor() {}

  public async startup() {
    dataStorageManager.subscribeBucket(BucketDefaultIDs.VIAJES, (data) => {
      this.bucketData = data
      this.storageOperations.forceRefresh()
    })
    this.storageOperations = dataHubManager.registerStorage(
      viajesHubName,
      async () => {
        return this.bucketData
      },
      {
        invalidationStrategy: 'hybrid',
        checkTime: defaultRefreshTime,
        lazyLoad: true,
        pipelines: [
          {
            name: ViajesHubPipelines.VIAJES_WITH_CORRECTED_DATES,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'correctDates',
                execute: (data: IViaje[]) => {
                  const correctedViajes = this.addCorrectedDatesToViajes(data)
                  return correctedViajes
                },
              },
            ],
          },
          {
            name: ViajesHubPipelines.VIAJES_WITH_REQUESTS,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addRequests',
                execute: (data: IViaje[]) => {
                  const dataWithRequests = this.addRequestToViajes(data)
                  return dataWithRequests
                },
              },
            ],
          },
          {
            name: ViajesHubPipelines.VIAJES_WITH_POSICIONAMIENTOS,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addPosicionamientos',
                execute: (data: OperatedViaje[]) => {
                  const dataWithChanges = this.addPosicionamientosToViajes(data)
                  return dataWithChanges
                },
              },
            ],
          },
          {
            name: ViajesHubPipelines.VIAJES_WITH_ASOCIATIONS,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addAsociations',
                execute: (data: OperatedViaje[]) => {
                  const dataWithAsociations = this.addAsociationsToViajes(data)
                  return dataWithAsociations
                },
              },
            ],
          },
          {
            name: ViajesHubPipelines.VIAJES_WITH_EMBARCACION,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addEmbarcacion',
                execute: (data: OperatedViaje[]) => {
                  const dataWithEmbarcacion = this.addEmbarcacionToViajes(data)
                  return dataWithEmbarcacion
                },
              },
            ],
          },
          {
            name: ViajesHubPipelines.VIAJES_WITH_PATRON,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addPatron',
                execute: (data: OperatedViaje[]) => {
                  const dataWithPatron = this.addPatronToViajes(data)
                  return dataWithPatron
                },
              },
            ],
          },
          {
            name: ViajesHubPipelines.VIAJES_WITH_ARMADOR,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addArmador',
                execute: (data: OperatedViaje[]) => {
                  const dataWithArmador = this.addArmadorToViajes(data)
                  return dataWithArmador
                },
              },
            ],
          },
          {
            name: ViajesHubPipelines.VIAJES_WITH_TRIPULANTES,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addTripulantes',
                execute: (data: OperatedViaje[]) => {
                  const dataWithTripulantes = this.addTripulantesToViajes(data)
                  return dataWithTripulantes
                },
              },
            ],
          },
          {
            name: ViajesHubPipelines.VIAJES_WITH_PERMISO_MUELLE_WESTE,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addPermisoMuelleWeste',
                execute: (data: OperatedViaje[]) => {
                  const dataWithPermisoMuelleWeste = this.addPermisoMuelleWesteToViajes(data)
                  return dataWithPermisoMuelleWeste
                },
              },
            ],
          },
          {
            name: ViajesHubPipelines.VIAJES_WITH_COBRO,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addCobro',
                execute: (data: OperatedViaje[]) => {
                  const dataWithCobro = this.addCobroToViajes(data)
                  return dataWithCobro
                },
              },
            ],
          },
          {
            name: ViajesHubPipelines.VIAJES_WITH_COBRO_REQUEST,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addCobro Request',
                execute: (data: OperatedViaje[]) => {
                  const dataWithCobroRequest = this.addCobroRequestToViajes(data)
                  return dataWithCobroRequest
                },
              },
            ],
          },
        ],
      },
    )

    dataStorageManager.subscribeBucket(BucketDefaultIDs.SOLICITUDES, () => {
      this.storageOperations.forceRefresh()
    })
    dataStorageManager.subscribeBucket(BucketDefaultIDs.ARMADORES, () => {
      this.storageOperations.forceRefresh()
    })
    dataStorageManager.subscribeBucket(BucketDefaultIDs.TRIPULANTES, () => {
      this.storageOperations.forceRefresh()
    })
    dataStorageManager.subscribeBucket(BucketDefaultIDs.COBROS, () => {
      this.storageOperations.forceRefresh()
    })
  }

  private addCorrectedDatesToViajes(viajes: IViaje[]): Array<IViaje> {
    const operatedViajes: Array<IViaje> = viajes.map((viaje) => {
      if (typeof viaje.marca_salida == 'object') {
        viaje.marca_salida = null
      }
      if (viaje.marca_fecha_inicio) {
        viaje.marca_fecha_inicio =
          new Date(viaje.marca_fecha_inicio).getFullYear() <= 1980 ? null : viaje.marca_fecha_inicio
      }
      if (viaje.marca_fecha_fin) {
        viaje.marca_fecha_fin = new Date(viaje.marca_fecha_fin).getFullYear() <= 1980 ? null : viaje.marca_fecha_fin
      }
      if (viaje.marca_salida) {
        viaje.marca_salida = new Date(viaje.marca_salida).getFullYear() <= 1980 ? null : viaje.marca_salida
      }
      return viaje
    })
    return operatedViajes
  }

  private addRequestToViajes(viajes: IViaje[]): Array<OperatedViaje> {
    const requestsList = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const viajesRequest = requestsList.filter((req) => req.tipo_objeto === RequestObjectType.PatronViaje)
    const operatedViajes: Array<OperatedViaje> = viajes.map((viaje) => {
      const requestData = viajesRequest
        .filter((req) => req.id_objeto.toString() === viaje.id.toString())
        .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0]
      return {
        ...viaje,
        requestData: requestData,
        posicionamientosData: [],
        asociationData: [],
        embarcacionData: undefined,
        patronData: undefined,
        armadorData: undefined,
        tripulantesData: [],
        permisoMuelleWesteData: undefined,
        cobroAplicado: undefined,
        cobroRequestData: undefined,
      }
    })
    return operatedViajes
  }

  private addPosicionamientosToViajes(viajes: OperatedViaje[]): Array<OperatedViaje> {
    const requestsList = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const posicionamientosRequest = requestsList.filter(
      (req) => req.tipo_objeto === RequestObjectType.PosicionamientoViaje,
    )
    const operatedViajes: Array<OperatedViaje> = viajes.map((viaje) => {
      const posicionamientosData = posicionamientosRequest.filter(
        (req) => req.id_objeto.toString() === viaje.id.toString(),
      )
      return {
        ...viaje,
        posicionamientosData: posicionamientosData,
      }
    })
    return operatedViajes
  }

  private addAsociationsToViajes(viajes: OperatedViaje[]): Array<OperatedViaje> {
    const requestsList = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const asociationsRequest = requestsList.filter((req) => req.tipo_objeto === RequestObjectType.Preacreditacion)
    const operatedViajes: Array<OperatedViaje> = viajes.map((viaje) => {
      const asociationData = asociationsRequest.filter((req) => req.id_objeto.toString() === viaje.id.toString())
      return {
        ...viaje,
        asociationData: asociationData,
      }
    })
    return operatedViajes
  }

  private addEmbarcacionToViajes(viajes: OperatedViaje[]): Array<OperatedViaje> {
    const embarcacionesList =
      dataStorageManager.getCurrentBucketData<IEmbarcacion>(BucketDefaultIDs.EMBARCACIONES) || []
    const operatedViajes: Array<OperatedViaje> = viajes.map((viaje) => {
      const embarcacionData = embarcacionesList.find((e) => e.id.toString() === viaje.embarcacion_id.toString())
      return {
        ...viaje,
        embarcacionData: embarcacionData,
      }
    })
    return operatedViajes
  }

  private addPatronToViajes(viajes: OperatedViaje[]): Array<OperatedViaje> {
    const tripulantesList = dataStorageManager.getCurrentBucketData<ITripulante>(BucketDefaultIDs.TRIPULANTES) || []
    const operatedViajes: Array<OperatedViaje> = viajes.map((viaje) => {
      if (!viaje.requestData) {
        return viaje
      }

      const patronData: ITripulante | undefined = tripulantesList.find(
        (t) => t.id.toString() === viaje.requestData!.id_objeto_2?.toString(),
      )
      let patronRequested: requestedTripulante | undefined
      if (patronData) {
        const patronAsociation = viaje.asociationData.find(
          (a) =>
            a.tipo_objeto_2 === RequestObjectType.Tripulante && a.id_objeto_2?.toString() == patronData?.id.toString(),
        )
        patronRequested = {
          ...patronData,
          requestData: patronAsociation,
        }
      }
      return {
        ...viaje,
        patronRequested: patronRequested,
        patronData: patronData as requestedTripulante | undefined,
      }
    })
    return operatedViajes
  }

  private addArmadorToViajes(viajes: OperatedViaje[]): Array<OperatedViaje> {
    const operatedViajes: Array<OperatedViaje> = viajes.map((viaje) => {
      const armadorRequested = viaje.asociationData.find(
        (a) =>
          a.tipo_objeto_2 === RequestObjectType.Armador && a.id_objeto_2?.toString() == viaje.id_armador?.toString(),
      )
      return {
        ...viaje,
        armadorData: armadorRequested,
      }
    })
    return operatedViajes
  }

  private addTripulantesToViajes(viajes: OperatedViaje[]): Array<OperatedViaje> {
    const tripulantesList = dataStorageManager.getCurrentBucketData<ITripulante>(BucketDefaultIDs.TRIPULANTES) || []
    const operatedViajes: Array<OperatedViaje> = viajes.map((viaje) => {
      const tripulantesData: Array<requestedTripulante> = tripulantesList
        .filter((t) => viaje.asociationData.some((a) => a.id_objeto_2?.toString() == t.id.toString()))
        .filter((t) => t.id.toString() !== viaje.patronData?.id.toString())
        .map((t) => {
          const tripulanteAsociation = viaje.asociationData.find(
            (a) => a.tipo_objeto_2 === RequestObjectType.Tripulante && a.id_objeto_2?.toString() == t.id.toString(),
          )
          return {
            ...t,
            requestData: tripulanteAsociation,
          }
        })
      return {
        ...viaje,
        tripulantesData: tripulantesData,
      }
    })
    return operatedViajes
  }

  private addPermisoMuelleWesteToViajes(viajes: OperatedViaje[]): Array<OperatedViaje> {
    const requestsList = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const permisosMuelleWesteRequest = requestsList.filter(
      (req) => req.tipo_objeto === RequestObjectType.PermisoMuelleWeste,
    )
    const operatedViajes: Array<OperatedViaje> = viajes.map((viaje) => {
      const permisoMuelleWesteData = permisosMuelleWesteRequest
        .filter((req) => req.id_objeto.toString() === viaje.id.toString())
        .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0]
      return {
        ...viaje,
        permisoMuelleWesteData: permisoMuelleWesteData,
      }
    })
    return operatedViajes
  }

  private addCobroToViajes(viajes: OperatedViaje[]): Array<OperatedViaje> {
    const cobrosList = dataStorageManager.getCurrentBucketData<any>(BucketDefaultIDs.COBROS) || []
    const operatedViajes: Array<OperatedViaje> = viajes.map((viaje) => {
      const cobroAplicado = cobrosList.find((c) => c.id.toString() === viaje.id.toString())
      return {
        ...viaje,
        cobroAplicado: cobroAplicado,
      }
    })
    return operatedViajes
  }

  private addCobroRequestToViajes(viajes: OperatedViaje[]): Array<OperatedViaje> {
    const requestsList = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const asociationCobros = requestsList.filter(
      (req) => req.tipo_objeto === RequestObjectType.Cobro && req.tipo_objeto_2 === 'viaje',
    )
    const operatedViajes: Array<OperatedViaje> = viajes.map((viaje) => {
      const cobroRequestData = asociationCobros.find(
        (c) => c.id_objeto_2 && c.id_objeto_2.toString() === viaje.id.toString(),
      )
      return {
        ...viaje,
        cobroRequestData: cobroRequestData,
      }
    })
    return operatedViajes
  }
}
